/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { getDevicePixelRatio } from "dc-preview-utils/src/Utils/DeviceUtils";
import { getCurrentPageIndex } from "selectors/pageNumber";
import { DEFAULT_MIN_ZOOM, DEFAULT_MAX_ZOOM } from "constants/constants";
import { getTotalHeightTillPageTop } from "selectors/pagedViewState";
import { isSinglePageMode, isFullScreenOrFitOnePageMode } from "selectors/documentSelectors";
import { getPageHeight, getPageWidth } from "../selectors/pageSelectors";
import { getDocumentViewHandle } from "selectors/documentViewSelectors";

export const getPageViewModifier = stores => stores.zoom.pageViewModifier;
export const getMinZoom = stores => stores.zoom.minZoom;
export const getMaxZoom = stores => stores.zoom.maxZoom;
export const shouldAllowInitialZoomLevelChange = stores => stores.zoom.allowInitialZoomlevelChange;
export const getDeviceZoomLevel = (stores, pageIndex) => getPageZoomLevel(stores, pageIndex) * window.devicePixelRatio;

export const getCurrentPageZoomLevel = stores => getPageZoomLevel(stores, getCurrentPageIndex(stores));

export const getPageZoomLevel = (stores, pageIndex) => stores.zoom.getCurrPageZoomLevel(pageIndex);

export const getMargin = stores => stores.zoom.fixedMargins;
export const getProvidedMargin = stores => stores.zoom.providedFixedWidthMargin;

export const getPinchZoomCenterParameters = stores => stores.zoom.pinchZoomStore;

export const getPinchZoomInitialZoom = stores => stores.zoom.pinchZoomStore.initialZoomLevel;

export const getPinchZoomScrollLeft = stores => {
  const { left } = getDocumentViewHandle(stores).getBoundingClientRect();
  const { centerX, centerPageIndex, centerXRatio } = getPinchZoomCenterParameters(stores);
  return left + -1 * (centerX - centerXRatio * getPageWidth(stores, centerPageIndex));
};

export const getPinchZoomScrollTop = stores => {
  const { centerY, centerPageIndex, centerYRatio } = getPinchZoomCenterParameters(stores);
  let newYPos = !(isFullScreenOrFitOnePageMode(stores) || isSinglePageMode(stores))
    ? getTotalHeightTillPageTop(stores, centerPageIndex)
    : 0;
  newYPos += centerYRatio * getPageHeight(stores, centerPageIndex);
  newYPos -= centerY;
  return newYPos;
};

export const getMinZoomLevelForMobile = (
  stores,
  docMinZoom = DEFAULT_MIN_ZOOM,
  docMaxZoom = DEFAULT_MAX_ZOOM,
  pageIndex = -1
) => {
  const fitWidthZoomLevel = stores.zoom.getFitWidthZoomLevel() * getDevicePixelRatio();
  const fitPageZoomLevel = stores.zoom.getFitPageZoomLevel(pageIndex) * getDevicePixelRatio();
  return Math.max(Math.min(docMaxZoom, fitWidthZoomLevel, fitPageZoomLevel), docMinZoom);
};

export const getIsCommentsPanelVisible = stores => stores.zoom.isCommentsPanelVisible;

export const isMaxZoomReached = stores =>
  getCurrentPageZoomLevel(stores) === getMaxZoom(stores) / getDevicePixelRatio();

export const isMinZoomReached = stores =>
  getCurrentPageZoomLevel(stores) === getMinZoom(stores) / getDevicePixelRatio();
